import { MutableRefObject, useEffect, useMemo, useState } from 'react'
import useIsMountedRef from 'use-is-mounted-ref'
import objectHash from 'object-hash'
import { ServiceMessage } from '../Components/Organisms/WelcomeContainer/Types'
import { MessageTypes, MessageTypesOrder } from '../Components/Organisms/WelcomeContainer/Constants'
import { ReactState } from '../../React/Common/Types/Types'
import { getConfig } from '../../React/Common/API/GenericConfigAPI'
import { displayError } from '../../React/Common/Utils/Messages'

export const useMessages = (): Nullable<ServiceMessage[]> => {
    const isMounted: MutableRefObject<boolean> = useIsMountedRef()

    const [messages, setMessages]: ReactState<Nullable<ServiceMessage[]>> = useState<Nullable<ServiceMessage[]>>()

    const processedMessages: Nullable<ServiceMessage[]> = useMemo(
        (): ServiceMessage[] => [
            ...(messages
                ?.sort(({ type: aType }: ServiceMessage, { type: bType }: ServiceMessage): number => {
                    // Order should be ERROR, WARNING, INFO, SUCCESS
                    const aOrder: number = MessageTypesOrder[aType as MessageTypes]
                    const bOrder: number = MessageTypesOrder[bType as MessageTypes]

                    return aOrder - bOrder
                })
                ?.map(
                    (serviceMessage: ServiceMessage): ServiceMessage => ({
                        ...serviceMessage,
                        id: objectHash(serviceMessage)
                    })
                )
                ?.filter((serviceMessage: ServiceMessage): boolean => {
                    const startDate: Date = new Date(serviceMessage.startDate)
                    const expiryDate: Date = new Date(serviceMessage.expiryDate)

                    return startDate <= new Date() && expiryDate >= new Date()
                }) ?? [])
        ],
        [JSON.stringify(messages)]
    )

    useEffect((): void => {
        getConfig<{ messages: ServiceMessage[] }>('service-messages')
            .then(
                (
                    response: Nullable<{
                        messages: ServiceMessage[]
                    }>
                ): void => {
                    if (isMounted.current) {
                        setMessages(response?.messages ?? [])
                    }
                }
            )
            .catch(displayError)
    }, [])

    return processedMessages
}
